import { useAuth } from "@/components/auth/auth-context.tsx";
import { useGetCurrentEscalation } from "@/lib/api/escalations.ts";
import { useGetMentorship } from "@/lib/api/mentorships.ts";
import { UserRole } from "@/lib/api/user.ts";
import { usePopupStore } from "@/lib/hooks/conversations/use-popup-store.ts";
import { cn } from "@/lib/utils.ts";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useLocation } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useState } from "react";
import { EscalationDetails } from "./escalation-details.tsx";
import { EscalationPopup } from "./escalation-popup.tsx";
import { EscalationRiskAssessment } from "./escalation-risk-assessment.tsx";
import { EscalationSeverityConfirmation } from "./escalation-severity-confirmation.tsx";

interface EscalationProps {
  mentorshipId: string;
}

type EscalationStep =
  | "severity"
  | "details"
  | "risk-assessment"
  | "confirmed"
  | null;

export function Escalation({ mentorshipId }: EscalationProps) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { escalationPopupOpen, setEscalationPopupOpen } = usePopupStore();
  const [currentStep, setCurrentStep] = useState<EscalationStep>(null);

  const { data: mentorship, isLoading: isMentorshipLoading } = useGetMentorship(
    mentorshipId!,
  );

  const {
    data: escalation,
    isLoading,
    refetch,
  } = useGetCurrentEscalation(mentorship?.mentee?.id);

  const isAdminViewingTheEscalation =
    pathname.includes(`/mentorships/${mentorshipId}`) &&
    user?.role !== UserRole.MENTEE &&
    user?.role !== UserRole.MENTOR;

  const handleClick = () => {
    refetch().then(({ data }) => {
      if (data?.status !== "OPEN") {
        setCurrentStep("severity");
      } else if (isAdminViewingTheEscalation) {
        setCurrentStep("details");
      } else if (data?.reason === "911") {
        setCurrentStep("details");
      } else {
        setCurrentStep("risk-assessment");
      }
    });

    setEscalationPopupOpen(!escalationPopupOpen);
    if (currentStep !== null) {
      setCurrentStep(null);
    } else if (escalation?.status !== "OPEN") {
      setCurrentStep("severity");
    } else if (isAdminViewingTheEscalation) {
      setCurrentStep("details");
    } else if (escalation?.reason === "911") {
      setCurrentStep("details");
    } else {
      setCurrentStep("risk-assessment");
    }
  };

  const handleClose = () => {
    setCurrentStep(null);
    setEscalationPopupOpen(false);
  };

  const getPopupTitle = () => {
    if (!currentStep && escalation?.status === "OPEN") {
      return "Active Escalation";
    }
    switch (currentStep) {
      case "severity":
        return "Start an escalation";
      case "details":
      case "risk-assessment":
        return "Active Escalation";
      default:
        return "Escalation";
    }
  };

  const getPopupHeight = () => {
    switch (currentStep) {
      case "severity":
        return "400px";
      case "details":
        return "570px";
      case "risk-assessment":
        return "580px";
      default:
        return "400px";
    }
  };

  const getPopupWidth = () => {
    switch (currentStep) {
      case "severity":
        return "500px";
      case "details":
        return "500px";
      case "risk-assessment":
        return "500px";
      default:
        return "500px";
    }
  };

  const renderEscalationContent = () => {
    // Show severity confirmation for new escalations
    if (currentStep === "severity" || (!currentStep && !escalation?.status)) {
      return (
        <EscalationSeverityConfirmation
          mentorship={mentorship!}
          onClose={handleClose}
          onThreatConfirm={() => setCurrentStep("details")}
          onThreatCancel={() => setCurrentStep("risk-assessment")}
        />
      );
    }

    // Show details for active escalations
    if (
      currentStep === "details" ||
      (!currentStep && escalation?.status === "OPEN")
    ) {
      return (
        <EscalationDetails
          mentorship={mentorship!}
          escalation={escalation!}
          onClose={handleClose}
        />
      );
    }

    // Show risk assessment when needed
    if (currentStep === "risk-assessment" && escalation) {
      return (
        <EscalationRiskAssessment
          menteeId={mentorship?.mentee?.id as string}
          escalationId={escalation.id}
          onClose={handleClose}
          onUpdate={() => setCurrentStep("details")}
        />
      );
    }

    // Fallback to severity confirmation if no other condition matches
    return (
      <EscalationSeverityConfirmation
        mentorship={mentorship!}
        onClose={handleClose}
        onThreatConfirm={() => setCurrentStep("details")}
        onThreatCancel={() => setCurrentStep("risk-assessment")}
      />
    );
  };

  if (isMentorshipLoading) return null;

  return (
    <div className={`fixed bottom-4 right-5 z-40`}>
      <div className="cursor-move">
        <button
          id="escalation-trigger"
          onClick={handleClick}
          disabled={isLoading}
          className={cn(
            "flex h-14 w-14 items-center justify-center rounded-full border shadow-lg hover:bg-red-100 hover:text-red-500 active:bg-red-200 active:text-red-600",
            {
              "bg-red-500 text-white hover:bg-red-600 hover:text-white active:bg-red-600 active:text-white":
                escalation?.status === "OPEN",
              "bg-white text-slate-900": escalation?.status !== "OPEN",
            },
          )}
        >
          {escalationPopupOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <ExclamationTriangleIcon className="h-6 w-6" />
          )}
        </button>
      </div>

      <EscalationPopup
        isOpen={escalationPopupOpen}
        onClose={handleClick}
        position={"right"}
        title={getPopupTitle()}
        height={getPopupHeight()}
        width={getPopupWidth()}
      >
        {renderEscalationContent()}
      </EscalationPopup>
    </div>
  );
}
